import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Button, Alert, Box } from '@mui/material'

import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { viewPdfDialogStyles } from './view-pdf-dialog.styles'

// Configurar worker de PDF.js (versión legacy para mejor compatibilidad)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

/**
 * @typedef {object} ViewPDFDialogProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {function(): void} [close] - Callback ejecutado para cerrar el visor de PDF.
 * @property {string | number} [docId] - ID del documento, usado cuando se navega por múltiples PDFs.
 * @property {string | File | Blob} file - El archivo PDF a mostrar (puede ser URL, objeto File o Blob).
 * @property {function(boolean): void} [setDisabled] - Callback para establecer el estado disabled de algún control padre.
 * @property {function(string | number): void} [setMultipleArray] - Callback ejecutado al llegar a la última página con un docId.
 * @property {boolean} [shortcutEnabled=false] - Si es true, muestra un botón para ir directamente a la última página.
 */

/**
 * Un componente visor de PDFs con navegación página por página.
 *
 * Proporciona una interfaz para visualizar documentos PDF con:
 * - Navegación secuencial página por página
 * - Botones de Anterior/Siguiente
 * - Opción de ir directamente a la última página
 * - Indicador de carga para cada página
 * - Gestión de estado para múltiples documentos
 * - Cierre automático al finalizar la última página
 *
 * @component
 * @example
 * const MyPDFViewer = () => {
 *   const [viewerOpen, setViewerOpen] = useState(false);
 *   const [pdfFile, setPdfFile] = useState(null);
 *
 *   const handleOpenPDF = (file) => {
 *     setPdfFile(file);
 *     setViewerOpen(true);
 *   };
 *
 *   const handleClose = () => {
 *     setViewerOpen(false);
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={() => handleOpenPDF('/documents/sample.pdf')}>
 *         Ver PDF
 *       </Button>
 *
 *       {viewerOpen && (
 *         <ViewPDFDialog
 *           file={pdfFile}
 *           close={handleClose}
 *           shortcutEnabled={true}
 *           setDisabled={(disabled) => console.log('Disabled:', disabled)}
 *         />
 *       )}
 *     </>
 *   );
 * };
 *
 * @param {ViewPDFDialogProps} props - Las props del componente ViewPDFDialog.
 */
export const ViewPDFDialog = withStyles(viewPdfDialogStyles)(({
  classes,
  close = () => {},
  docId = null,
  file,
  setDisabled,
  setMultipleArray = null,
  shortcutEnabled = false,
  warnMessage,
}) => {
  const { formatMessage } = useIntl()
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [isLoading, setIsLoading] = useState(true)

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
  }

  const handleNextPage = () => {
    setIsLoading(true)
    if (pageNumber < numPages) setPageNumber(pageNumber + 1)
    else {
      if (!docId) {
        setDisabled(false)
        close()
      } else {
        setMultipleArray(docId)
        close()
      }
    }
  }

  const handlePrevPage = () => {
    setIsLoading(true)
    if (pageNumber > 1) setPageNumber(pageNumber - 1)
    else {
      close()
    }
  }

  const handleLastPage = () => {
    setIsLoading(true)
    setPageNumber(numPages)
  }

  return (
    <>
      <div className={classes.alertContainer}>
        <Alert severity="warning" className={classes.alert}>
          {formatMessage({
            id: warnMessage || 'pages.profile.company.guild.info.publicOffer.pdfReaded.warning',
          })}
        </Alert>
      </div>
      <Document
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={(error) => {
          console.error('Error loading PDF:', error)
          setIsLoading(false)
        }}
        className={classes.pdfWrapper}
        loading={<div>Cargando PDF...</div>}
      >
        <Page
          pageNumber={pageNumber}
          scale={1.2}
          onLoadSuccess={() => setIsLoading(false)}
          onLoadError={(error) => {
            console.error('Error loading page:', error)
            setIsLoading(false)
          }}
          loading={<div>Cargando página...</div>}
        />
      </Document>
      {!isLoading ? (
        <div className={classes.pdfButtonsWrapper}>
          <Button
            variant="contained"
            color="primary"
            className={classes.pdfButton}
            onClick={handlePrevPage}
          >
            {pageNumber === 1
              ? formatMessage({ id: 'global.close' })
              : formatMessage({ id: 'global.previous' })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.pdfButton}
            onClick={handleNextPage}
          >
            {pageNumber === numPages
              ? formatMessage({ id: 'global.accept' })
              : formatMessage({ id: 'global.next' })}
          </Button>
          {shortcutEnabled && pageNumber !== numPages ? (
            <Button
              variant="contained"
              color="secondary"
              className={classes.pdfButton}
              onClick={handleLastPage}
            >
              {formatMessage({ id: 'global.goLastPage' })}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  )
})
